import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu, Avatar, Dropdown, Space, Typography, theme, Button } from 'antd';
import { useScrollToTop } from '../hooks/useScrollToTop';
import { useActivityTracker } from '../hooks/useActivityTracker';
import type { MenuProps } from 'antd';
import { MenuOutlined } from '@ant-design/icons';
import {
  SettingOutlined,
  TeamOutlined,
  EnvironmentOutlined,
  ToolOutlined,
  UserOutlined,
  LogoutOutlined,
  LeftCircleOutlined,
  RightCircleOutlined,
  AlertOutlined,
  CalendarOutlined,
  FileTextOutlined,
  HistoryOutlined,
  HomeOutlined,
  BarChartOutlined,
  CreditCardOutlined,
  AppstoreOutlined,
  CheckSquareOutlined,
  ShoppingCartOutlined,
  LineChartOutlined,
  QuestionCircleOutlined,
  FormOutlined,
} from '@ant-design/icons';
import { useAuthStore } from '../stores/authStore';
import { trpc, queryClient } from '../lib/api';
import { useUserPreferences } from '../contexts/UserPreferencesContext';
import { NotificationBell } from '../components/notification/NotificationBell';
import { DevRouteInfo } from '../components/common/DevRouteInfo';
import { getEffectiveAvatarUrl } from '../utils/avatar';
import { HelpModal } from '../components/help/HelpModal';
import { useHelpStore } from '../stores/helpStore';
import { FONT_FAMILY_BASE } from '../constants/fonts';
import { useResponsive, LAYOUT_CONSTANTS } from '../hooks/useResponsive';
import { MobileDrawerMenu } from '../components/layout/MobileDrawerMenu';
import { AppBreadcrumb, BreadcrumbProvider } from '../components/Breadcrumb';
import { getEnvStyles } from '../constants/environment';
import { PageMemoDrawer, PAGE_MEMO_DRAWER_WIDTH } from '../components/memo/PageMemoDrawer';
import { resolvePageMemoInfo } from '../constants/pageMemoPages';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;
const { useToken } = theme;
const MEMO_DRAWER_WIDTH_STORAGE_KEY = 'pageMemoDrawerWidth';

export function MainLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useToken();
  const { user, clearAuth, isAuthenticated } = useAuthStore();
  const { preferences, effectiveTheme } = useUserPreferences();
  const { openModal: openHelpModal } = useHelpStore();
  const { data: profile } = trpc.user.getProfile.useQuery(undefined, {
    enabled: isAuthenticated,
  });
  const { data: company } = trpc.company.getCurrentCompany.useQuery(undefined, {
    enabled: isAuthenticated,
    staleTime: 30000, // 30秒間キャッシュ
  });

  // サブスクリプションが期限切れ（canceled）かどうか
  const isSubscriptionExpired = company?.subscriptionStatus === 'canceled';
  const collapsedPreferenceRef = useRef(preferences.appearance.compactSidebar);
  const [collapsed, setCollapsed] = useState(() => collapsedPreferenceRef.current);

  // レスポンシブ対応
  const { isMobile, useMobileMenu } = useResponsive();
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [memoDrawerOpen, setMemoDrawerOpen] = useState(false);
  const [memoDrawerWidth, setMemoDrawerWidth] = useState(() => {
    if (typeof window === 'undefined') return PAGE_MEMO_DRAWER_WIDTH;
    const stored = Number(window.localStorage.getItem(MEMO_DRAWER_WIDTH_STORAGE_KEY));
    return Number.isFinite(stored) && stored > 0 ? stored : PAGE_MEMO_DRAWER_WIDTH;
  });

  // ページ遷移時にスクロール位置をトップにリセット
  const contentRef = useScrollToTop<HTMLDivElement>();

  // ユーザー活動追跡
  useActivityTracker();

  // サブメニューがないためopenKeysは空配列
  const getOpenKeys = useCallback((): string[] => {
    return [];
  }, []);

  const [openKeys, setOpenKeys] = useState<string[]>(() => getOpenKeys());
  const pageMemoInfo = useMemo(
    () => resolvePageMemoInfo(location.pathname),
    [location.pathname]
  );

  // 現在のパスに応じたデフォルトヘルプトピックを決定
  const getDefaultHelpTopicId = useCallback((): string | undefined => {
    const path = location.pathname;

    // 設備管理系
    if (path.startsWith('/app/equipments')) return 'ledger-equipments';
    if (path.startsWith('/app/locations')) return 'ledger-locations';
    if (path.startsWith('/app/deterioration')) return 'equipment-deterioration';

    // 書類
    if (path.startsWith('/app/documents/official-inspections')) return 'documents-official-inspections';
    if (path === '/app/documents') return 'ledger-documents';
    if (path.startsWith('/app/documents/')) return 'documents-manage';

    // メンテナンス系
    if (path.startsWith('/app/maintenance')) return 'maintenance-calendar';
    if (path.startsWith('/app/my-tasks')) return 'maintenance-my-tasks';
    if (path.startsWith('/app/work-history')) return 'maintenance-work-history';
    if (path.startsWith('/app/work-order-templates')) return 'maintenance-work-order';

    // 緊急作業
    if (path.startsWith('/app/emergency-works')) return 'emergency-overview';

    // 部品・消耗品・在庫
    if (path.startsWith('/app/stock-dashboard')) return 'maintenance-stock-dashboard';
    if (path.startsWith('/app/purchase-orders')) return 'maintenance-purchase-orders';
    if (path.startsWith('/app/part-consumption-logs')) return 'maintenance-part-consumption-logs';
    if (path.startsWith('/app/parts-analytics')) return 'maintenance-parts-analytics';
    if (path.startsWith('/app/parts')) return 'ledger-parts';
    if (path.startsWith('/app/suppliers')) return 'ledger-suppliers';

    // ユーザー管理
    if (path.startsWith('/app/user-management')) return 'user-management-overview';
    if (path === '/app/profile') return 'user-management-profile';

    // サブスクリプション
    if (path.startsWith('/app/subscription')) return 'subscription-overview';

    // 設定
    if (path.startsWith('/app/settings')) return 'getting-started-first-steps';

    // ダッシュボード
    if (path === '/app/dashboard') return 'getting-started-dashboard';

    // デフォルト
    return undefined;
  }, [location.pathname]);

  useEffect(() => {
    if (collapsedPreferenceRef.current !== preferences.appearance.compactSidebar) {
      collapsedPreferenceRef.current = preferences.appearance.compactSidebar;   
      setCollapsed(preferences.appearance.compactSidebar);
    }
  }, [preferences.appearance.compactSidebar]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(
      MEMO_DRAWER_WIDTH_STORAGE_KEY,
      String(memoDrawerWidth)
    );
  }, [memoDrawerWidth]);

  // パスが変更されたときにサブメニューを更新
  useEffect(() => {
    setOpenKeys(getOpenKeys());
  }, [getOpenKeys]);

  const isDarkTheme = effectiveTheme === 'dark';
  const isMildTheme = effectiveTheme === 'mild';
  const contentPadding = isMobile
    ? LAYOUT_CONSTANTS.CONTENT_PADDING_MOBILE
    : preferences.appearance.compactSidebar ? 16 : 24;
  const headerHeight = isMobile
    ? LAYOUT_CONSTANTS.HEADER_HEIGHT_MOBILE
    : LAYOUT_CONSTANTS.HEADER_HEIGHT;
  const memoDrawerOffset = memoDrawerOpen && !isMobile ? memoDrawerWidth : 0;
  const contentPaddingRight = contentPadding + memoDrawerOffset;
  const headerBackground = isDarkTheme ? '#141414' : isMildTheme ? '#fff3d6' : '#fff';
  const headerTextColor = isDarkTheme ? '#f0f0f0' : isMildTheme ? '#5a4633' : '#000';
  const headerBoxShadow = isDarkTheme ? '0 1px 4px rgba(0,0,0,0.45)' : isMildTheme ? '0 2px 6px rgba(208, 173, 118, 0.35)' : '0 1px 4px rgba(0,21,41,.08)';
  const layoutBackground = isDarkTheme ? '#0f0f0f' : isMildTheme ? '#f8f4e9' : '#EEEEEE';
  const contentCardBackground = isDarkTheme ? '#1f1f1f' : isMildTheme ? '#fffaf0' : '#fff';
  const contentBoxShadow = isDarkTheme ? '0 1px 6px rgba(0,0,0,0.35)' : isMildTheme ? '0 2px 8px rgba(208, 173, 118, 0.26)' : '0 1px 4px rgba(0,21,41,.08)';

  const logoutMutation = trpc.auth.logout.useMutation({
    onSuccess: () => {
      // React Query のキャッシュを完全にクリア（重要：前のユーザーのデータを残さない）
      // invalidate() ではなく clear() を使用 - invalidate() は stale マークのみで実データは残る
      queryClient.clear();
      clearAuth();
      navigate('/login');
    },
  });

  const handleLogout = () => {
    logoutMutation.mutate();
  };

  const canManageCompanySettings = ['admin', 'manager'].includes(
    (profile?.role ?? user?.role) ?? '',
  );
  const headerAvatarUrl = useMemo(() => getEffectiveAvatarUrl(profile), [profile]);
  const headerDisplayName = profile?.name || user?.name || user?.email || profile?.email;

  const menuItems = useMemo<MenuProps['items']>(() => {
    // サブスクリプションが期限切れの場合は「契約プラン」のみ表示
    if (isSubscriptionExpired) {
      return [
        { key: '/app/subscription', icon: <CreditCardOutlined />, label: '契約プラン' },
      ];
    }

    const items: MenuProps['items'] = [
      // メイン
      { key: '/app/dashboard', icon: <HomeOutlined />, label: 'ダッシュボード' },

      // スケジュール
      {
        type: 'group',
        label: 'スケジュール',
        children: [
          { key: '/app/maintenance', icon: <CalendarOutlined />, label: '保全カレンダー' },
          { key: '/app/my-tasks', icon: <CheckSquareOutlined />, label: 'マイタスク' },
          { key: '/app/work-order-templates', icon: <FileTextOutlined />, label: '作業指示書' },
          { key: '/app/work-history', icon: <HistoryOutlined />, label: '作業履歴' },
          { key: '/app/emergency-works', icon: <AlertOutlined />, label: '緊急作業' },
        ],
      },

      // 在庫管理
      {
        type: 'group',
        label: '部品・消耗品在庫管理',
        children: [
          { key: '/app/stock-dashboard', icon: <BarChartOutlined />, label: '在庫ダッシュボード' },
          { key: '/app/purchase-orders', icon: <ShoppingCartOutlined />, label: '発注管理' },
          { key: '/app/part-consumption-logs', icon: <HistoryOutlined />, label: '消費管理' },
          { key: '/app/parts-analytics', icon: <LineChartOutlined />, label: '部品・消耗品分析' },
        ],
      },

      // 予測・分析
      {
        type: 'group',
        label: '予測・分析',
        children: [
          { key: '/app/deterioration', icon: <BarChartOutlined />, label: '健全度予測' },
        ],
      },

      // 台帳管理
      {
        type: 'group',
        label: '台帳管理',
        children: [
          { key: '/app/locations', icon: <EnvironmentOutlined />, label: 'エリア' },
          { key: '/app/equipments', icon: <ToolOutlined />, label: '設備' },
          { key: '/app/parts', icon: <AppstoreOutlined />, label: '部品・消耗品' },
          { key: '/app/suppliers', icon: <TeamOutlined />, label: '業者' },
          { key: '/app/documents', icon: <FileTextOutlined />, label: '書類' },
        ],
      },
    ];

    return items;
  }, [isSubscriptionExpired]);

  // 設定ページへ遷移するハンドラー
  const handleSettingsClick = () => {
    navigate('/app/settings');
  };

  const userMenuItems: MenuProps['items'] = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'プロフィール',
      onClick: () => navigate('/app/profile'),
    },
    {
      key: 'memos',
      icon: <FormOutlined />,
      label: '一元メモ',
      onClick: () => navigate('/app/memos'),
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'ログアウト',
      onClick: handleLogout,
    },
  ];

  // サイドバーの幅計算（モバイル時は0）
  const sidebarWidth = useMobileMenu ? 0 : (collapsed ? LAYOUT_CONSTANTS.SIDEBAR_COLLAPSED_WIDTH : LAYOUT_CONSTANTS.SIDEBAR_WIDTH);

  // 環境スタイル（本番: 青→紫グラデーション）
  const envStyle = getEnvStyles(
    'linear-gradient(135deg, #1890ff 0%, #722ed1 100%)',
    'transparent'
  );

  // メニュークリック時のハンドラー
  const handleMenuClick = (key: string) => {
    if (key.startsWith('/app/')) {
      navigate(key);
    }
  };

  return (
    <Layout style={{ height: '100vh', overflow: 'hidden' }}>
      {/* モバイル用ドロワーメニュー */}
      {useMobileMenu && (
        <MobileDrawerMenu
          open={mobileDrawerOpen}
          onClose={() => setMobileDrawerOpen(false)}
          menuItems={menuItems}
          selectedKeys={[location.pathname]}
          openKeys={openKeys}
          onOpenChange={(keys) => setOpenKeys(keys)}
          onMenuClick={handleMenuClick}
          title="一元帳"
          subtitle={envStyle.isProduction ? 'ICHIGEN-CHO' : `ICHIGEN-CHO | ${envStyle.label}`}
          headerBackground={envStyle.background}
        />
      )}

      {/* デスクトップ用サイドバー */}
      {!useMobileMenu && (
        <Sider
          collapsible
          collapsed={collapsed}
          onCollapse={setCollapsed}
          trigger={null}
          theme="light"
          breakpoint="lg"
          style={{
            height: '100vh',
            position: 'fixed',
            left: 0,
            top: 0,
            bottom: 0,
            zIndex: 100,
            overflow: 'hidden',
          }}
        >
          <div style={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}>
            {/* タイトル部分（固定） */}
            <div style={{
              height: 64,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderBottom: `1px solid ${token.colorBorderSecondary}`,
              background: envStyle.background,
              padding: collapsed ? '12px 8px' : '12px 16px',
              flexShrink: 0,
            }}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontWeight: 700,
                  lineHeight: 1.05,
                  textAlign: 'center',
                  gap: collapsed ? 2 : 4,
                  fontFamily: FONT_FAMILY_BASE,
                  transition: 'all 0.2s ease',
                }}
              >
                <span
                  style={{
                    fontSize: collapsed ? 18 : 22,
                    letterSpacing: collapsed ? '0.05em' : '0.05em',
                    whiteSpace: 'nowrap',
                  }}
                >
                  一元帳
                </span>
                <span
                  style={{
                    fontSize: collapsed ? 8 : 10,
                    letterSpacing: '-0.0em',
                    whiteSpace: 'nowrap',
                  }}
                >
                  ICHIGEN-CHO
                </span>
                {/* 環境情報表示（本番以外） */}
                {!envStyle.isProduction && (
                  <span
                    style={{
                      fontSize: collapsed ? 6 : 8,
                      opacity: 0.8,
                      marginTop: collapsed ? 0 : 2,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {envStyle.label}
                  </span>
                )}
              </div>
            </div>

            {/* メニュー部分（スクロール可能） */}
            <div
              className="sidebar-menu-scroll"
              style={{
                flex: 1,
                overflowY: 'auto',
                overflowX: 'hidden',
                background: envStyle.menuTint,
              }}
            >
              <Menu
                theme="light"
                mode="inline"
                selectedKeys={[location.pathname]}
                openKeys={openKeys}
                onOpenChange={(keys) => setOpenKeys(keys as string[])}
                items={menuItems}
                onClick={({ key }) => {
                  if (key.startsWith('/app/')) {
                    navigate(key);
                  }
                }}
                style={{
                  borderRight: 0,
                  paddingTop: 8,
                  background: 'transparent',
                }}
              />
            </div>
          </div>
          <style
            dangerouslySetInnerHTML={{
              __html: `
              .ant-menu-submenu > .ant-menu-submenu-title {
                font-weight: 500 !important;
              }
              .ant-menu-item {
                margin-bottom: 1px !important;
              }
              .ant-menu-submenu {
                margin-bottom: 1px !important;
              }
              /* グループヘッダーのスタイル */
              .ant-menu-item-group-title {
                font-size: 11px !important;
                color: ${token.colorTextSecondary} !important;
                padding: 8px 16px 2px !important;
                text-transform: uppercase !important;
                letter-spacing: 0.5px !important;
                border-top: 1px solid ${token.colorBorderSecondary} !important;
                margin-top: 4px !important;
              }
              /* 最初のグループは上線なし */
              .ant-menu-item-group:first-child .ant-menu-item-group-title {
                border-top: none !important;
                margin-top: 0 !important;
              }
              /* スクロールバーを非表示 */
              .sidebar-menu-scroll {
                -ms-overflow-style: none !important;
                scrollbar-width: none !important;
              }
              .sidebar-menu-scroll::-webkit-scrollbar {
                display: none !important;
                width: 0 !important;
                height: 0 !important;
              }
              /* Sider内部のレイアウト調整 */
              .ant-layout-sider-children {
                display: flex !important;
                flex-direction: column !important;
                height: 100% !important;
              }
            `
            }}
          />
        </Sider>
      )}

      <Layout style={{
        marginLeft: sidebarWidth,
        transition: 'margin-left 0.2s',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: layoutBackground,
        color: headerTextColor,
      }}>
        <Header style={{
          padding: `0 ${contentPadding}px`,
          background: headerBackground,
          color: headerTextColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: headerBoxShadow,
          position: 'fixed',
          top: 0,
          right: memoDrawerOffset,
          left: sidebarWidth,
          height: headerHeight,
          lineHeight: `${headerHeight}px`,
          zIndex: 99,
          transition: 'left 0.2s, right 0.2s, background 0.2s',
        }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {/* モバイル: ハンバーガーメニュー */}
            {useMobileMenu ? (
              <MenuOutlined
                style={{ fontSize: 20, cursor: 'pointer', padding: 8 }}
                onClick={() => setMobileDrawerOpen(true)}
              />
            ) : (
              /* デスクトップ: サイドバー折り畳みトグル */
              collapsed ? (
                <RightCircleOutlined
                  style={{ fontSize: 18, cursor: 'pointer' }}
                  onClick={() => setCollapsed(false)}
                />
              ) : (
                <LeftCircleOutlined
                  style={{ fontSize: 18, cursor: 'pointer' }}
                  onClick={() => setCollapsed(true)}
                />
              )
            )}
          </div>

          <Space size={isMobile ? 8 : 16} align="center">
            <NotificationBell />
            <Button
              type="text"
              icon={<FormOutlined />}
              onClick={() => setMemoDrawerOpen((prev) => !prev)}
              aria-label="メモ"
            />
            {/* ヘルプアイコン */}
            <Button
              type="text"
              icon={<QuestionCircleOutlined />}
              onClick={() => openHelpModal(getDefaultHelpTopicId())}
              aria-label="ヘルプ"
            />
            {/* 設定アイコン */}
            {canManageCompanySettings && (
              <Button
                type="text"
                icon={<SettingOutlined />}
                onClick={handleSettingsClick}
                aria-label="設定"
              />
            )}
            <Dropdown menu={{ items: userMenuItems }} trigger={['click']}>
              <Space style={{ cursor: 'pointer', color: headerTextColor }}>
                <Avatar
                  src={headerAvatarUrl ?? undefined}
                  icon={!headerAvatarUrl && <UserOutlined />}
                  size={isMobile ? 'default' : 'default'}
                />
                {/* モバイル時はユーザー名を非表示 */}
                {!isMobile && (
                  <Text style={{ color: headerTextColor }}>
                    {headerDisplayName ?? '未設定'}
                  </Text>
                )}
              </Space>
            </Dropdown>
          </Space>
        </Header>

        <Content
          ref={contentRef}
          style={{
            marginTop: headerHeight,
            padding: contentPadding,
            paddingRight: contentPaddingRight,
            overflowY: 'auto',
            height: `calc(100vh - ${headerHeight}px)`,
            background: layoutBackground,
            transition: 'background 0.2s, padding-right 0.2s',
          }}
        >
          <div style={{
            background: contentCardBackground,
            borderRadius: isMobile ? 4 : 8,
            padding: contentPadding,
            minHeight: `calc(100vh - ${headerHeight + contentPadding * 2}px)`,
            boxShadow: contentBoxShadow,
            color: headerTextColor,
            transition: 'background 0.2s',
          }}>
            <BreadcrumbProvider>
              <AppBreadcrumb layoutType="app" />
              <Outlet />
            </BreadcrumbProvider>
          </div>
        </Content>

        {/* 開発環境でのみ表示されるファイルパス情報 */}
        <DevRouteInfo />

        {/* 一元メモドロワー */}
        <PageMemoDrawer
          open={memoDrawerOpen}
          onClose={() => setMemoDrawerOpen(false)}
          pageKey={pageMemoInfo?.pageKey ?? null}
          pageLabel={pageMemoInfo?.label}
          width={memoDrawerWidth}
          onWidthChange={setMemoDrawerWidth}
          resizable={!isMobile}
        />

        {/* ヘルプモーダル */}
        <HelpModal />
      </Layout>
    </Layout>
  );
}
